import LoyaltyAccountListView from "issara-sdk/apis/LoyaltyAccountListView_apps_CRM";
import LoyaltyTierListView from "issara-sdk/apis/LoyaltyTierListView_apps_CRM";
import ProxyPatientHasDivisionAdminList from "issara-sdk/apis/ProxyPatientHasDivisionAdminList_apps_PRX";
import LoyaltyAccountSerializer from "issara-sdk/types/LoyaltyAccountSerializer_apps_CRM";
import LoyaltyTierPolicySerializer from "issara-sdk/types/LoyaltyTierPolicySerializer_apps_CRM";
import ProxyPatientHasDivisionAdminSerializer from "issara-sdk/types/ProxyPatientHasDivisionAdminSerializer_apps_PRX";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD_CUSTOMER: "LOAD_CUSTOMER",
  LOAD_TIERS: "LOAD_TIERS",
} as const;

export const SEQUENCE_NAME = "RegistrationV4" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  RegistrationV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    tiers: TierMap;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

/** ระดับสมาชิกของผู้ป่วย, tierIndex = ลำดับสี 0-3 */
export type TierBadge = { tier: string; tierIndex: number };

/** ระดับสมาชิกต่อผู้ป่วย: undefined = ยังไม่โหลด, null = ยังไม่เป็นสมาชิก */
export type TierMap = Record<number, TierBadge | null>;

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD_CUSTOMER]: { patientId: number };
  /** refresh: โหลดใหม่แม้เคยโหลดแล้ว */
  [ACTIONS.LOAD_TIERS]: { patientIds: number[]; refresh?: boolean };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  RegistrationV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      RegistrationV4Sequence: {
        ...state.RegistrationV4Sequence,
        sequenceIndex: "Action",
        tiers: state.RegistrationV4Sequence?.tiers ?? {},
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD_CUSTOMER]: HandleLoadCustomer,
    [ACTIONS.LOAD_TIERS]: HandleLoadTiers,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleLoadTiers: Handler<Params<Actions["LOAD_TIERS"]>> = async (controller, params) => {
  let state = controller.getState();

  const known = state.RegistrationV4Sequence?.tiers ?? {};
  const patientIds = params.refresh
    ? params.patientIds
    : params.patientIds.filter((id) => known[id] === undefined);

  if (patientIds.length === 0) {
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_TIERS]: BLC_STATUS.LOADING },
  });

  const extra = { division: controller.data.division, device: controller.data.device };

  const [[tierRes, tierErr], [accountRes, accountErr]] = await Promise.all([
    LoyaltyTierListView.list({ apiToken: controller.apiToken, extra }),
    LoyaltyAccountListView.list({
      apiToken: controller.apiToken,
      params: { limit: 99999 },
      extra,
    }),
  ]);

  if (tierErr || accountErr) {
    SetErrorMessage(controller, {
      ...params,
      btnAction: BTN_ACTS.LOAD_TIERS,
      error: tierErr || accountErr,
    });

    return;
  }

  const accounts: LoyaltyAccountSerializer[] = accountRes?.items ?? [];
  const tierPolicies: LoyaltyTierPolicySerializer[] = tierRes?.items ?? [];

  state = controller.getState();

  const tiers: TierMap = { ...state.RegistrationV4Sequence?.tiers };

  for (const id of patientIds) {
    tiers[id] = getTierBadge(accounts, tierPolicies, id);
  }

  controller.setState({
    RegistrationV4Sequence: { ...state.RegistrationV4Sequence, tiers },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_TIERS]: BLC_STATUS.SUCCESS },
  });
};

/** ลูกค้า MePOS ของผู้ป่วย แล้วให้ MeBilling โหลดรายละเอียด (selectedCustomer ของหน้า POS) */
const HandleLoadCustomer: Handler<Params<Actions["LOAD_CUSTOMER"]>> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_CUSTOMER]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD_CUSTOMER]: null },
  });

  const [result, error] = await ProxyPatientHasDivisionAdminList.list({
    apiToken: controller.apiToken,
    params: { patient: params.patientId, limit: 1 },
    extra: { division: controller.data.division },
  });

  const customer: ProxyPatientHasDivisionAdminSerializer | undefined = result?.items?.[0];

  if (error || !customer?.id || !customer.proxy_patient) {
    SetErrorMessage(controller, {
      ...params,
      btnAction: BTN_ACTS.LOAD_CUSTOMER,
      error: error || "ไม่พบข้อมูลลูกค้าของผู้ป่วยในหน่วยนี้",
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.LOAD_CUSTOMER]: BLC_STATUS.SUCCESS,
    },
  });

  controller.handleEvent({
    message: "RunSequence",
    params: {
      sequence: "MeBilling",
      action: "GET_CUSTOMER_DETAIL",
      customerId: customer.id,
      proxyPatientId: customer.proxy_patient,
    },
  });
};

/** ระดับปัจจุบันของผู้ป่วย: เรียงระดับตาม start_point แล้วใช้ลำดับเป็นสี (0-3) */
export const getTierBadge = (
  accounts: LoyaltyAccountSerializer[],
  tierPolicies: LoyaltyTierPolicySerializer[],
  patientId: number,
): TierBadge | null => {
  const currentTier = accounts.find((account) => account.patient === patientId)?.current_tier;

  if (!currentTier?.name) {
    return null;
  }

  const tierIds = [...tierPolicies]
    .sort((a, b) => Number(a.start_point) - Number(b.start_point))
    .map((tier) => tier.id);

  return {
    tier: currentTier.name,
    tierIndex: Math.min(3, Math.max(0, tierIds.indexOf(currentTier.id))),
  };
};
